import {
  LOADING,
  UPLOADING,
  SET_CURRENT_PAGE,
  CHANGE_LOCALE,
  SET_ASPECT_RATIO,
  START_REQUEST,
  END_REQUEST,
  SHOW_ERROR,
  SHOW_INFO,
  SHOW_PROMPT,
  SHOW_CONFIRM,
  TOGGLE_HELP,
  CHANGE_CURRENT_PAGE_TITLE,
  SET_HELP_COORDS,
} from './types';
import { getTranslateFunc, axiosAPI, isMobileAppRuntime } from 'utils';
import { ensureWebAdminLocaleStrings } from 'actions/commonActions';
import { isOnline } from 'mobile-modules/offline/state';

export const loading = (state) => ({
  filter: 'status',
  type: LOADING,
  payload: state,
});

export const changePageTitle = (payload) => ({
  filter: 'status',
  type: CHANGE_CURRENT_PAGE_TITLE,
  payload: payload,
});

export const uploading = (state) => ({
  // For s3 daownloads
  filter: 'status',
  type: UPLOADING,
  payload: state,
});

export const startRequest = (requestId) => ({
  filter: 'status',
  type: START_REQUEST,
  payload: requestId,
});

export const endRequest = (requestId) => ({
  filter: 'status',
  type: END_REQUEST,
  payload: requestId,
});

export const setCurrentPage = (match) => ({
  filter: 'status',
  type: SET_CURRENT_PAGE,
  payload: match,
});

/*export const changeLocale = (localeCode) => (dispatch, getState) => {
  const { common } = getState();
  const { strings, locales } = common;

  const locale = locales.filter((loc) => loc.code === localeCode)[0];
  const translate = getTranslateFunc(strings, locale.code);

  localStorage.setItem('localeCode', localeCode);

  axiosAPI.defaults.headers['User-Locale'] = localeCode;

  dispatch({
    filter: 'status',
    type: CHANGE_LOCALE,
    payload: { locale, translate },
  });
};*/

export const changeLocale = (localeCode) => (dispatch, getState) => {
  const { common } = getState();
  const uiCode = String(localeCode || 'en').trim().toLowerCase();
  if (!isMobileAppRuntime() && common?.adminLoadedLocales
    && !common.adminLoadedLocales.includes(uiCode)) {
    return dispatch(ensureWebAdminLocaleStrings(uiCode))
      .then(() => dispatch(changeLocale(uiCode)))
      .catch((error) => {
        // Keep the previous UI language when this locale could not be loaded.
        console.error('[web-admin-runtime] UI locale fetch failed', error);
        return false;
      });
  }
  const { strings = {}, locales = [] } = common || {};

  const requestedLocale = locales.find((loc) => loc?.code === localeCode);
  const fallbackLocale = locales.find((loc) => loc?.code === 'en');
  const locale = requestedLocale || fallbackLocale || locales[0] || { code: 'en' };

  console.log('[statusActions][changeLocale]', {
    requestedLocaleCode: localeCode,
    resolvedLocaleCode: locale.code,
    availableLocales: locales.map((loc) => loc?.code).filter(Boolean),
    usedFallback: !requestedLocale,
  });

  const translate = getTranslateFunc(strings, locale.code);

  localStorage.setItem('localeCode', locale.code);
  axiosAPI.defaults.headers['User-Locale'] = locale.code;

  dispatch({
    filter: 'status',
    type: CHANGE_LOCALE,
    payload: { locale, translate },
  });
};

const MOBILE_DIALOG_DIAGNOSTIC_PATH = 'flc-offline-v1/diagnostics/missing-assets.jsonl';

const normalizeDialogPayload = payload => (
  typeof payload === 'string' ? { message: payload } : (payload || {})
);

const mobileOfflineDialogDiagnostic = (kind, payload, extra = {}) => {
  if (typeof window === 'undefined' || !isMobileAppRuntime() || isOnline()) return;

  const fs = window.Capacitor?.Plugins?.FLCOfflineStorage
    || window.Capacitor?.Plugins?.Filesystem
    || null;
  if (!fs?.appendText) return;

  const data = normalizeDialogPayload(payload);
  const stack = String(new Error().stack || '')
    .split('\n')
    .slice(2, 10)
    .join(' | ');
  const record = {
    time: new Date().toISOString(),
    type: 'offline-ui-dialog-action',
    kind,
    title: data.title == null ? '' : String(data.title),
    message: data.message == null ? '' : String(data.message),
    html: Boolean(data.html),
    pathname: window.location?.pathname || '',
    stack,
    ...extra,
  };

  Promise.resolve(fs.appendText({
    path: MOBILE_DIALOG_DIAGNOSTIC_PATH,
    text: `${JSON.stringify(record)}\n`,
  })).catch(() => {});
};

const isBlankDialogMessage = payload => {
  const data = normalizeDialogPayload(payload);
  if (data.html) {
    return !String(data.message == null ? '' : data.message)
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .trim();
  }
  return !String(data.message == null ? '' : data.message).trim();
};

export const showError = (error) => {
  mobileOfflineDialogDiagnostic('error', error, {
    blank: isBlankDialogMessage(error),
  });
  return {
    filter: 'status',
    type: SHOW_ERROR,
    payload: error,
  };
};

export const showInfo = (info) => {
  const mobileOffline = typeof window !== 'undefined' && isMobileAppRuntime() && !isOnline();
  const blank = isBlankDialogMessage(info);

  mobileOfflineDialogDiagnostic('info', info, { blank });

  // Mobile/offline only: a content-less Material info dialog has no user value
  // and blocks navigation. Keep desktop/online behavior byte-for-byte equivalent.
  if (mobileOffline && info && blank) {
    mobileOfflineDialogDiagnostic('info', info, {
      blank: true,
      suppressed: true,
      reason: 'empty-message',
    });
    return {
      filter: 'status',
      type: SHOW_INFO,
      payload: null,
    };
  }

  return {
    filter: 'status',
    type: SHOW_INFO,
    payload: info,
  };
};

export const showPrompt = (params) => {
  mobileOfflineDialogDiagnostic('prompt', params, {
    blank: isBlankDialogMessage(params),
  });
  return {
    filter: 'status',
    type: SHOW_PROMPT,
    payload: params,
  };
};

export const showPromptPromise = (params) => (dispatch) => {
  return new Promise((resolve, reject) => {
    dispatch({
      filter: 'status',
      type: SHOW_PROMPT,
      payload: { ...params, cb: resolve },
    });
  });
};

export const showConfirm = (params) => {
  mobileOfflineDialogDiagnostic('confirm', params, {
    blank: isBlankDialogMessage(params),
  });
  return {
    filter: 'status',
    type: SHOW_CONFIRM,
    payload: params,
  };
};

export const showConfirmPromise = (params) => (dispatch) => {
  return new Promise((resolve, reject) => {
    dispatch({
      filter: 'status',
      type: SHOW_CONFIRM,
      payload: { ...params, cb: resolve },
    });
  });
};

export const toggleHelp = () => ({
  filter: 'status',
  type: TOGGLE_HELP,
});

export const setHelpCoords = (e, draggableEvent) => {
  return {
    filter: 'status',
    type: SET_HELP_COORDS,
    payload: {
      x: draggableEvent.x,
      y: draggableEvent.y,
    },
  };
};

export const setAspectRatio = (aspectRatio) => {
  // const body = document.querySelector("body");

  // if (aspectRatio >= 1.25) {
  //   body.classList.add("landscape");
  // } else {
  //   body.classList.remove("landscape");
  // }

  return {
    filter: 'status',
    type: SET_ASPECT_RATIO,
    payload: aspectRatio,
  };
};
